/**
 * Ingreso de los empleados en la página de Cognito del directorio interno: código de autorización con
 * PKCE (RFC 7636), el flujo recomendado para aplicaciones de navegador (RFC 9700). La consola nunca ve la
 * contraseña. Solo el verificador y el estado viven en sessionStorage, mientras dura el viaje de ida y
 * vuelta; los tokens quedan en memoria.
 */
import { z } from "zod";

export interface ConfigOauth {
  /** https://<prefijo>.auth.<región>.amazoncognito.com */
  readonly dominio: string;
  readonly clienteId: string;
  /** Dirección de la consola: se vuelve a `<origen>/ingreso` y, al salir, a `<origen>/`. */
  readonly origen: string;
}

export interface Tokens {
  /** Token de identidad: la API lo exige porque trae el correo y los grupos del empleado. */
  readonly idToken: string;
  readonly refreshToken: string | null;
  /** Instante (ms) en que vence el token de identidad. */
  readonly venceEn: number;
}

export interface Retorno {
  readonly tokens: Tokens;
  /** Ruta de la consola que se pidió antes de ir a ingresar. */
  readonly volver: string;
}

/** Proveedor de identidad: el real (Cognito) o el de la demostración. */
export interface ProveedorIdentidad {
  iniciar(volver: string): Promise<void>;
  completar(busqueda: string): Promise<Retorno>;
  renovar(refreshToken: string): Promise<Tokens>;
  salir(refreshToken: string | null): Promise<void>;
}

export type MotivoError = "cancelado" | "estado" | "canje";

export class ErrorIngreso extends Error {
  constructor(readonly motivo: MotivoError) {
    super(motivo);
    this.name = "ErrorIngreso";
  }
}

export interface Almacen {
  leer(clave: string): string | null;
  guardar(clave: string, valor: string): void;
  borrar(clave: string): void;
}

/** sessionStorage puede no estar (modo privado estricto): sin él no se puede completar el ingreso. */
export function almacenDeSesion(): Almacen {
  return {
    leer: (k) => {
      try {
        return sessionStorage.getItem(k);
      } catch {
        return null;
      }
    },
    guardar: (k, v) => {
      try {
        sessionStorage.setItem(k, v);
      } catch {
        // sin almacenamiento: al volver, el estado no coincidirá y se pedirá ingresar de nuevo
      }
    },
    borrar: (k) => {
      try {
        sessionStorage.removeItem(k);
      } catch {
        // nada que borrar
      }
    },
  };
}

export interface DependenciasOauth {
  readonly fetch: typeof globalThis.fetch;
  readonly almacen: Almacen;
  readonly ir: (url: string) => void;
  readonly ahora: () => number;
  readonly aleatorio?: (n: number) => Uint8Array;
}

const CLAVE = "admin:ingreso";
const ALCANCES = "openid email profile";

export function base64url(bytes: Uint8Array): string {
  let s = "";
  for (const b of bytes) s += String.fromCharCode(b);
  return btoa(s).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

export async function desafioPkce(verificador: string): Promise<string> {
  const huella = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(verificador));
  return base64url(new Uint8Array(huella));
}

/** Solo rutas internas de la consola: nunca otro sitio (redirección abierta). */
export function rutaSegura(volver: string | null | undefined): string {
  if (!volver?.startsWith("/") || volver.startsWith("//") || volver.startsWith("/\\")) return "/";
  if (volver.startsWith("/ingreso")) return "/";
  return volver;
}

const esquemaPendiente = z.object({ estado: z.string(), verificador: z.string(), volver: z.string() });
const esquemaTokens = z.object({
  id_token: z.string(),
  refresh_token: z.string().optional(),
  expires_in: z.number(),
});

export function crearCognito(cfg: ConfigOauth, deps: DependenciasOauth): ProveedorIdentidad {
  const aleatorio = deps.aleatorio ?? ((n: number) => crypto.getRandomValues(new Uint8Array(n)));
  const redireccion = `${cfg.origen}/ingreso`;

  async function token(cuerpo: Record<string, string>): Promise<Tokens> {
    const r = await deps.fetch(`${cfg.dominio}/oauth2/token`, {
      method: "POST",
      headers: { "content-type": "application/x-www-form-urlencoded" },
      body: new URLSearchParams({ client_id: cfg.clienteId, ...cuerpo }).toString(),
    });
    if (!r.ok) throw new ErrorIngreso("canje");
    const t = esquemaTokens.safeParse(await r.json());
    if (!t.success) throw new ErrorIngreso("canje");
    return {
      idToken: t.data.id_token,
      refreshToken: t.data.refresh_token ?? cuerpo.refresh_token ?? null,
      venceEn: deps.ahora() + t.data.expires_in * 1000,
    };
  }

  return {
    async iniciar(volver) {
      const verificador = base64url(aleatorio(48));
      const estado = base64url(aleatorio(24));
      deps.almacen.guardar(CLAVE, JSON.stringify({ estado, verificador, volver: rutaSegura(volver) }));
      const q = new URLSearchParams({
        response_type: "code",
        client_id: cfg.clienteId,
        redirect_uri: redireccion,
        scope: ALCANCES,
        state: estado,
        code_challenge: await desafioPkce(verificador),
        code_challenge_method: "S256",
      });
      deps.ir(`${cfg.dominio}/oauth2/authorize?${q.toString()}`);
    },

    async completar(busqueda) {
      const q = new URLSearchParams(busqueda);
      const guardado = deps.almacen.leer(CLAVE);
      deps.almacen.borrar(CLAVE);
      if (q.get("error")) throw new ErrorIngreso("cancelado");
      let pendiente: z.output<typeof esquemaPendiente> | null = null;
      try {
        const r = esquemaPendiente.safeParse(JSON.parse(guardado ?? "null"));
        pendiente = r.success ? r.data : null;
      } catch {
        pendiente = null;
      }
      const codigo = q.get("code");
      // El estado protege contra que otro sitio inyecte su propio código (CSRF del ingreso).
      if (!pendiente || !codigo || q.get("state") !== pendiente.estado) throw new ErrorIngreso("estado");
      const tokens = await token({
        grant_type: "authorization_code",
        code: codigo,
        redirect_uri: redireccion,
        code_verifier: pendiente.verificador,
      });
      return { tokens, volver: rutaSegura(pendiente.volver) };
    },

    renovar: (refreshToken) => token({ grant_type: "refresh_token", refresh_token: refreshToken }),

    async salir(refreshToken) {
      if (refreshToken) {
        // Revocar es lo correcto pero no puede impedir salir: si falla, igual se cierra la sesión de Cognito.
        await deps
          .fetch(`${cfg.dominio}/oauth2/revoke`, {
            method: "POST",
            headers: { "content-type": "application/x-www-form-urlencoded" },
            body: new URLSearchParams({ token: refreshToken, client_id: cfg.clienteId }).toString(),
          })
          .catch(() => undefined);
      }
      const q = new URLSearchParams({ client_id: cfg.clienteId, logout_uri: `${cfg.origen}/` });
      deps.ir(`${cfg.dominio}/logout?${q.toString()}`);
    },
  };
}
